(() => {
  const MOBILE_QUERY = '(max-width: 989px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const DETAILS_SELECTOR = '.footer-block__menu-accordion';
  const SUMMARY_SELECTOR = '.footer-block__menu-summary';
  const CONTENT_SELECTOR = '.footer-block__details-content';
  const DURATION_MS = 360;
  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

  const mobileQuery = window.matchMedia(MOBILE_QUERY);
  const reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  const trackedDetails = new Set();
  const detailsStates = new WeakMap();

  function isMobile() {
    return mobileQuery.matches;
  }

  function prefersReducedMotion() {
    return reducedMotionQuery.matches;
  }

  function cleanInlineState(details) {
    details.style.height = '';
    details.style.overflow = '';
  }

  function setExpandedState(details, expanded) {
    const summary = details.querySelector(SUMMARY_SELECTOR);
    if (summary instanceof HTMLElement) {
      summary.setAttribute('aria-expanded', String(expanded));
    }
  }

  function getAccordionGroupRoot(details) {
    return details.closest('.footer__blocks-wrapper') || details.closest('footer') || document;
  }

  function getScrollContainer(element) {
    let node = element?.parentElement;

    while (node && node !== document.body) {
      const styles = window.getComputedStyle(node);
      const overflowY = styles.overflowY;
      const canScroll =
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
        && node.scrollHeight > node.clientHeight;
      if (canScroll) return node;
      node = node.parentElement;
    }

    return window;
  }

  function getTopInContainer(element, scrollContainer) {
    const rect = element.getBoundingClientRect();
    if (scrollContainer === window) return rect.top;
    return rect.top - scrollContainer.getBoundingClientRect().top;
  }



  function closeSiblingAccordions(currentDetails) {
    const groupRoot = getAccordionGroupRoot(currentDetails);

    groupRoot.querySelectorAll(DETAILS_SELECTOR).forEach((details) => {
      if (details === currentDetails || !details.open) {
        return;
      }

      const state = detailsStates.get(details);
      if (state?.collapse) {
        state.collapse();
      } else {
        details.open = false;
        setExpandedState(details, false);
        cleanInlineState(details);
      }
    });
  }

  function enforceSingleOpenPerGroup(root = document) {
    if (!isMobile()) return;

    const firstOpenByGroup = new Map();

    root.querySelectorAll(`${DETAILS_SELECTOR}[open]`).forEach((details) => {
      const groupRoot = getAccordionGroupRoot(details);

      if (!firstOpenByGroup.has(groupRoot)) {
        firstOpenByGroup.set(groupRoot, details);
        setExpandedState(details, true);
        return;
      }

      detailsStates.get(details)?.cancelAnimation?.();
      details.open = false;
      setExpandedState(details, false);
      cleanInlineState(details);
    });
  }

  function bindAccordion(details) {
    if (!(details instanceof HTMLElement) || trackedDetails.has(details)) {
      return;
    }

    const summary = details.querySelector(SUMMARY_SELECTOR);
    const content = details.querySelector(CONTENT_SELECTOR);
    if (!(summary instanceof HTMLElement) || !(content instanceof HTMLElement)) {
      return;
    }

    setExpandedState(details, details.open);

    let animation = null;
    let isClosing = false;
    let isExpanding = false;

    const cancelAnimation = () => {
      if (animation) {
        animation.cancel();
        animation = null;
      }
      isClosing = false;
      isExpanding = false;
      cleanInlineState(details);
    };

    const onAnimationFinish = (open) => {
      details.open = open;
      setExpandedState(details, open);
      animation = null;
      isClosing = false;
      isExpanding = false;
      cleanInlineState(details);
    };

    const onAnimationCancel = () => {
      isClosing = false;
      isExpanding = false;
    };

    const animateDetailsHeight = (startHeightPx, endHeightPx, openAfter) => {
      cancelAnimation();

      animation = details.animate(
        {
          height: [`${startHeightPx}px`, `${endHeightPx}px`],
        },
        {
          duration: DURATION_MS,
          easing: EASING,
        }
      );

      animation.onfinish = () => onAnimationFinish(openAfter);
      animation.oncancel = onAnimationCancel;
    };

    const expand = () => {
      isExpanding = true;
      details.open = true;

      const startHeight = summary.offsetHeight;
      details.style.height = `${startHeight}px`;
      details.style.overflow = 'hidden';

      window.requestAnimationFrame(() => {
        const endHeight = summary.offsetHeight + content.offsetHeight;
        animateDetailsHeight(startHeight, endHeight, true);
      });
    };

    const collapse = () => {
      isClosing = true;

      const startHeight = details.offsetHeight;
      const endHeight = summary.offsetHeight;
      details.style.overflow = 'hidden';

      animateDetailsHeight(startHeight, endHeight, false);
    };

    summary.addEventListener('click', async (event) => {
      if (!isMobile()) {
        return;
      }

      event.preventDefault();
      const shouldOpen = isClosing || !details.open;

      if (prefersReducedMotion() || typeof details.animate !== 'function') {
        cancelAnimation();
        if (shouldOpen) {
          closeSiblingAccordions(details);
        }
        details.open = shouldOpen;
        setExpandedState(details, shouldOpen);
        cleanInlineState(details);
        return;
      }

      if (shouldOpen) {
        closeSiblingAccordions(details);
        expand();
      } else if (isExpanding || details.open) {
        collapse();
      }
    });

    detailsStates.set(details, {cancelAnimation, expand, collapse});
    trackedDetails.add(details);
  }

  function initializeAccordions(root = document) {
    root.querySelectorAll(DETAILS_SELECTOR).forEach((details) => bindAccordion(details));
    enforceSingleOpenPerGroup(root);
  }

  function handleViewportChange() {
    if (!isMobile()) {
      trackedDetails.forEach((details) => cleanInlineState(details));
      return;
    }

    enforceSingleOpenPerGroup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAccordions(), { once: true });
  } else {
    initializeAccordions();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAccordions(event.target);
  });

  window.addEventListener('resize', handleViewportChange, { passive: true });
  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', handleViewportChange);
  } else if (typeof mobileQuery.addListener === 'function') {
    mobileQuery.addListener(handleViewportChange);
  }
})();
